import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdChevronLeft, MdChevronRight, MdAdd, MdClose, MdVideocam } from 'react-icons/md';
import attendanceAPI from '../../api/attendanceAPI';
import { useAttendanceAuth } from '../../context/AttendanceAuthContext';
import {
  CAMERA_DAY_START_HOUR,
  CAMERA_DAY_END_HOUR,
  CAMERA_HOUR_PX,
  addDays,
  addMinutesToBookingString,
  canCancelBooking,
  formatBookingDay,
  formatBookingRange,
  formatBookingTime,
  getCameraStateStyle,
  getWeekStart,
  hasCameraFacultyRole,
  hasCameraMediaRole,
  layoutDayBookings,
  toDayString,
  toRangeString,
} from '../../utils/cameraBookingUtils';

const HOURS = Array.from(
  { length: CAMERA_DAY_END_HOUR - CAMERA_DAY_START_HOUR },
  (_, i) => CAMERA_DAY_START_HOUR + i
);
const GRID_HEIGHT = HOURS.length * CAMERA_HOUR_PX;
const SLOT_MINUTES = 30;

/**
 * Camera Calendar: week view of the shared media camera.
 * Faculty admins can click an empty slot to request it. Media users see
 * everything. Other faculties' bookings show only their time and state.
 */
function CameraCalendar() {
  const navigate = useNavigate();
  const { user, getToken } = useAttendanceAuth();
  const isMedia = hasCameraMediaRole(user);
  // Faculty admins request for their faculty; media users book on a faculty's behalf.
  const canRequest = hasCameraFacultyRole(user) || isMedia;

  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const todayStr = toDayString(new Date());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await attendanceAPI.getCameraCalendar(
          {
            date_from: toRangeString(weekStart),
            date_to: toRangeString(addDays(weekStart, 7)),
          },
          getToken()
        );
        if (!cancelled) setBookings(result.bookings || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load the camera calendar.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // getToken is stable for the session; reload only when the week or a change requires it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, reloadKey]);

  const goToWeek = (offsetWeeks) => {
    setSelected(null);
    setWeekStart((current) => addDays(current, offsetWeeks * 7));
  };

  const goToToday = () => {
    setSelected(null);
    setWeekStart(getWeekStart(new Date()));
  };

  // Click an empty part of a day column to request a slot starting there.
  const handleSlotClick = (day, event) => {
    if (!canRequest) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const minutesFromStart = Math.floor((y / CAMERA_HOUR_PX) * (60 / SLOT_MINUTES)) * SLOT_MINUTES;
    const start = addMinutesToBookingString(
      `${toDayString(day)} ${String(CAMERA_DAY_START_HOUR).padStart(2, '0')}:00`,
      minutesFromStart
    );
    const end = addMinutesToBookingString(start, 60);
    navigate('/attendance/camera/request', { state: { start, end } });
  };

  const handleCancel = async () => {
    if (!selected) return;
    if (!window.confirm('Cancel this camera booking?')) return;
    setActionBusy(true);
    setActionError('');
    try {
      await attendanceAPI.cancelCameraBooking(selected.id, getToken());
      setSelected(null);
      setReloadKey((key) => key + 1);
    } catch (err) {
      setActionError(err.message || 'Could not cancel this booking.');
    } finally {
      setActionBusy(false);
    }
  };

  const selectedStyle = selected ? getCameraStateStyle(selected.state) : null;
  const canSeeDetails = selected && (selected.is_mine || isMedia);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <MdVideocam className="h-7 w-7 text-blue-500" />
          <h2 className="text-xl font-bold text-gray-800">Camera Calendar</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => goToWeek(-1)}
            className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            aria-label="Previous week"
          >
            <MdChevronLeft />
          </button>
          <button
            type="button"
            onClick={goToToday}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => goToWeek(1)}
            className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            aria-label="Next week"
          >
            <MdChevronRight />
          </button>
          <span className="text-sm font-medium text-gray-700 px-2">
            {formatBookingDay(`${toDayString(days[0])} 00:00`)} – {formatBookingDay(`${toDayString(days[6])} 00:00`)}
          </span>
          {canRequest && (
            <button
              type="button"
              onClick={() => navigate('/attendance/camera/request')}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
            >
              <MdAdd /> Request camera
            </button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
        <span className="flex items-center gap-2">
          <span className="inline-block w-4 h-4 rounded border-l-4 border-green-500 bg-green-100" /> Booked (approved)
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-4 h-4 rounded border-l-4 border-amber-500 bg-amber-100" /> Pending
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-4 h-4 rounded border border-gray-300 bg-white" /> Free
        </span>
        {canRequest && (
          <span className="text-gray-500">Click an empty slot to request it.</span>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      )}

      {/* Week grid */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        <div className="min-w-[720px]">
          <div className="flex border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="w-14 flex-shrink-0" />
            {days.map((day) => {
              const dayStr = toDayString(day);
              const isToday = dayStr === todayStr;
              return (
                <div
                  key={dayStr}
                  className={`flex-1 py-2 text-center text-sm font-medium border-l border-gray-200 ${
                    isToday ? 'text-blue-700 bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  {formatBookingDay(`${dayStr} 00:00`)}
                </div>
              );
            })}
          </div>

          <div className="flex">
            <div className="w-14 flex-shrink-0 relative" style={{ height: GRID_HEIGHT }}>
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="absolute right-2 text-xs text-gray-500 -translate-y-2"
                  style={{ top: (hour - CAMERA_DAY_START_HOUR) * CAMERA_HOUR_PX }}
                >
                  {String(hour).padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {days.map((day) => {
              const dayStr = toDayString(day);
              const { items, laneCount } = layoutDayBookings(bookings, dayStr);
              return (
                <div
                  key={dayStr}
                  className={`relative flex-1 border-l border-gray-200 ${canRequest ? 'cursor-pointer' : ''}`}
                  style={{ height: GRID_HEIGHT }}
                  onClick={(event) => handleSlotClick(day, event)}
                >
                  {HOURS.map((hour) => (
                    <div
                      key={hour}
                      className="absolute left-0 right-0 border-t border-gray-100 pointer-events-none"
                      style={{ top: (hour - CAMERA_DAY_START_HOUR) * CAMERA_HOUR_PX }}
                    />
                  ))}
                  {items.map(({ booking, startMin, endMin, lane }) => {
                    const style = getCameraStateStyle(booking.state);
                    const top = Math.max(0, ((startMin - CAMERA_DAY_START_HOUR * 60) / 60) * CAMERA_HOUR_PX);
                    const height = Math.max(((endMin - startMin) / 60) * CAMERA_HOUR_PX, 22);
                    const width = 100 / laneCount;
                    // Faculty admins see times only for other bookings; media sees the purpose.
                    const showPurpose = (booking.is_mine || isMedia) && booking.purpose;
                    const label = showPurpose
                      ? booking.purpose
                      : booking.state === 'approved'
                      ? 'Booked'
                      : style.label;
                    return (
                      <button
                        key={`${booking.id}-${dayStr}`}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setActionError('');
                          setSelected(booking);
                        }}
                        className={`absolute rounded border-l-4 px-1 py-0.5 text-left text-[11px] leading-tight overflow-hidden shadow-sm ${style.block} ${
                          selected?.id === booking.id ? 'ring-2 ring-blue-500' : ''
                        }`}
                        style={{ top, height, left: `${lane * width}%`, width: `calc(${width}% - 2px)` }}
                        title={`${formatBookingRange(booking.start, booking.end)} · ${style.label}`}
                      >
                        <div className="font-semibold">
                          {formatBookingTime(booking.start)}–{formatBookingTime(booking.end)}
                        </div>
                        <div className="truncate">{label}</div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading calendar…</p>}

      {/* Selected booking */}
      {selected && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${selectedStyle.badge}`}>
                  {selectedStyle.label}
                </span>
                {canSeeDetails && selected.reference && (
                  <span className="text-xs text-gray-500">{selected.reference}</span>
                )}
              </div>
              <p className="mt-2 font-medium text-gray-800">
                {formatBookingRange(selected.start, selected.end)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="p-1 text-gray-500 hover:text-gray-800"
              aria-label="Close details"
            >
              <MdClose />
            </button>
          </div>

          {canSeeDetails ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Purpose</dt>
                <dd className="text-gray-800">{selected.purpose || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Location</dt>
                <dd className="text-gray-800">{selected.location || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Faculty</dt>
                <dd className="text-gray-800">{selected.faculty?.name || 'None (media reservation)'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Requested by</dt>
                <dd className="text-gray-800">{selected.requested_by || '—'}</dd>
              </div>
              {selected.entered_by && (
                <div>
                  <dt className="text-gray-500">Entered by</dt>
                  <dd className="text-gray-800">{selected.entered_by}</dd>
                </div>
              )}
              {selected.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notes</dt>
                  <dd className="text-gray-800">{selected.notes}</dd>
                </div>
              )}
              {selected.reviewed_by && (
                <div>
                  <dt className="text-gray-500">Reviewed by</dt>
                  <dd className="text-gray-800">{selected.reviewed_by}</dd>
                </div>
              )}
              {selected.state === 'rejected' && selected.reject_reason && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Reject reason</dt>
                  <dd className="text-red-700">{selected.reject_reason}</dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="text-sm text-gray-600">
              {selected.state === 'approved' ? 'The camera is booked for this time.' : 'A request is pending for this time.'}
            </p>
          )}

          {actionError && (
            <div className="p-2 rounded bg-red-50 text-sm text-red-700">{actionError}</div>
          )}

          {(isMedia || selected.is_mine) && canCancelBooking(selected) && (
            <div>
              <button
                type="button"
                onClick={handleCancel}
                disabled={actionBusy}
                className="px-4 py-2 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {actionBusy ? 'Cancelling…' : 'Cancel booking'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CameraCalendar;
